/**
 * Workspace widgets in the Chats side column (ADR 0024 §3): Activity, Feed
 * and Active chats over the person's Scope, as compact one-line rows (no
 * avatar, no message quote), the newest three only; the expand button opens
 * the Workspace page for the rest. A row opens its chat on its own Account.
 */
import { useCallback, useMemo, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from '../chat/fanBlocks'
import type { SectionsOpen } from '../chat/sectionsOpen'
import { activityItems, activitySettingsFrom } from '../workspace/activity'
import { activeCards, activityCard, feedCards, type CardContext } from '../workspace/cards'
import { activeChatGroups, chatKey, type ActiveWindow } from '../workspace/chats'
import { colourMode as readColourMode } from '../workspace/colour'
import { useDismissed } from '../workspace/dismissed'
import { feedSettingsFrom, type FeedItem } from '../workspace/feed'
import { useActiveChats } from '../workspace/useActiveChats'
import { useFeed } from '../workspace/useFeed'
import { useStableCallback } from '../workspace/useStable'
import { useWorkspace, useWorkspaceLive } from '../workspace/WorkspaceProvider'
import type { CardItem } from '../workspace/widgets/ChatCollection'
import { FeatherIcon } from '../components/FeatherIcon'
import { Spinner } from '../components/Spinner'
import { useChatsDeps } from './ChatsDeps'
import type { OpenChat } from './sideBlocks'
import { ACTIVE_MODE_KEY, activeModeFrom, type ActiveMode } from './sideLayout'
import '../workspace/workspace.css'

/** Each dock widget shows this many rows; the Workspace page has the rest. */
const DOCK_ROWS = 3

interface DockProps {
  /** The chat open beside the column, highlighted when it is a card. */
  accountId: string
  fanId: string
  sections: SectionsOpen
  onOpenChat?: OpenChat
  /** The Workspace page, where these widgets show everything. */
  onOpenWorkspace?: () => void
}

/** What every dock widget shares: Scope, the card context, opening a card. */
function useDockBase({ accountId, fanId, onOpenChat }: DockProps) {
  const { t } = useTranslation('workspace')
  const { t: tc } = useTranslation('chat')
  const { now } = useChatsDeps()
  const ws = useWorkspace()
  const accountsById = useMemo(() => new Map(ws.accounts.map((a) => [a.account_id, a])), [ws.accounts])
  const scope = ws.settings.scope ?? 'all'
  const inScope = useMemo(() => {
    if (scope === 'all') return null
    const ids = new Set(scope.filter((id) => accountsById.has(id)))
    return ids.size ? ids : null
  }, [scope, accountsById])
  const nowMs = now().getTime()
  const colourMode = readColourMode(ws.settings.colourMode)
  const ctx = useMemo<CardContext>(() => ({ accounts: accountsById, nowMs, t, tc, colourMode }), [accountsById, nowMs, t, tc, colourMode])
  const open = useStableCallback((c: CardItem) => onOpenChat?.(c.accountId, c.fanId, c.fanName))
  return { t, ws, scope, inScope, ctx, open, openKey: chatKey(accountId, fanId) }
}

function DockSection({
  id,
  icon,
  label,
  sections,
  onOpenWorkspace,
  before,
  children,
}: {
  id: string
  icon: string
  label: string
  sections: SectionsOpen
  onOpenWorkspace?: () => void
  before?: React.ReactNode
  children: React.ReactNode
}) {
  const { t } = useTranslation('chat')
  return (
    <Section
      id={`dock:${id}`}
      icon={icon}
      label={label}
      sections={sections}
      tools={
        onOpenWorkspace && (
          <button type="button" className="rc-icon-btn" title={t('side.widget.expand', { widget: label })} aria-label={t('side.widget.expand', { widget: label })} onClick={onOpenWorkspace}>
            <FeatherIcon name="maximize-2" size={13} />
          </button>
        )
      }
    >
      {before}
      {children}
    </Section>
  )
}

interface DockEmpty {
  text: string
  loading?: boolean
}

/** The newest few cards as one-line rows: who, which model, what, when. */
function DockRows({ items, selectedKey, onOpen, empty, total, onOpenWorkspace }: { items: CardItem[]; selectedKey: string | null; onOpen: (c: CardItem) => void; empty: DockEmpty; total: number; onOpenWorkspace?: () => void }) {
  const { t } = useTranslation('chat')
  if (!items.length) return <div className="rc-dock-empty">{empty.loading ? <Spinner size={14} /> : <span className="rc-muted">{empty.text}</span>}</div>
  const more = total > items.length
  return (
    <div className="rc-dock-rows">
      {items.map((c) => {
        const kindColour = c.kind ? `oklch(0.8 0.1 ${c.kind.hue})` : undefined
        return (
          <div
            key={c.key}
            role="button"
            tabIndex={0}
            aria-label={`${c.fanName} · ${c.modelName}`}
            className={`rc-dock-row ${selectedKey === c.key ? 'is-selected' : ''}`}
            onClick={() => onOpen(c)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(c))}
          >
            <span className="rc-dock-bar" style={{ background: c.accent }} />
            {c.kind ? (
              <span className="rc-dock-kind" style={{ color: kindColour }} title={c.kind.label}>
                <FeatherIcon name={c.kind.icon} size={12} />
              </span>
            ) : (
              c.online && <span className="rc-dock-online" title={t('side.widget.online')} />
            )}
            <span className="rc-dock-fan">{c.fanName}</span>
            <span className="rc-dock-model" style={{ color: c.accent }}>
              {c.modelName}
            </span>
            <span className="rc-dock-flex" />
            {c.kind?.amount ? (
              <b className="rc-dock-amount" style={{ color: kindColour }}>
                {c.kind.amount}
              </b>
            ) : (
              c.spent && <b className="rc-dock-amount">{c.spent}</b>
            )}
            {c.time && <span className="rc-dock-time">{c.time}</span>}
            {c.action && (
              <button
                type="button"
                className="rc-dock-act"
                title={c.action.tip}
                aria-label={c.action.tip}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation()
                  c.action!.run()
                }}
              >
                <FeatherIcon name={c.action.icon} size={12} />
              </button>
            )}
          </div>
        )
      })}
      {more && onOpenWorkspace && (
        <button type="button" className="rc-dock-more" onClick={onOpenWorkspace}>
          {t('side.widget.more')}
        </button>
      )}
    </div>
  )
}

const ACTIVE_MODES: ActiveMode[] = ['replied', 'waiting']

export function ActivityDock(props: DockProps) {
  const { t, ws, inScope, ctx, open } = useDockBase(props)
  const { t: tc } = useTranslation('chat')
  const live = useWorkspaceLive()
  const { dismissed, dismiss } = useDismissed()
  const settings = useMemo(() => activitySettingsFrom(ws.settings.activity), [ws.settings.activity])
  const events = useMemo(() => (inScope ? live.events.filter((e) => inScope.has(e.account_id)) : live.events), [live.events, inScope])
  const items = useMemo(() => activityItems(events, settings, dismissed, ctx.nowMs), [events, settings, dismissed, ctx.nowMs])
  const dismissOne = useCallback((id: string) => dismiss([id]), [dismiss])
  const cards = useMemo(() => items.slice(0, DOCK_ROWS).map((item, i) => activityCard(item, i, ctx, dismissOne)), [items, ctx, dismissOne])
  return (
    <DockSection id="activity" icon="activity" label={t('widgets.activity')} sections={props.sections} onOpenWorkspace={props.onOpenWorkspace}>
      <DockRows
        items={cards}
        total={items.length}
        selectedKey={null}
        onOpen={open}
        onOpenWorkspace={props.onOpenWorkspace}
        empty={{ text: tc('side.widget.emptyActivity'), loading: !live.events.length && ws.status === 'connecting' }}
      />
    </DockSection>
  )
}

export function ActiveDock(props: DockProps) {
  const { t, ws, inScope, ctx, open, openKey } = useDockBase(props)
  const { t: tc } = useTranslation('chat')
  const activeWindow: ActiveWindow = (ws.settings.activeWindow as ActiveWindow) || 'today'
  const mode = activeModeFrom(ws.settings[ACTIVE_MODE_KEY])
  const { active, loaded } = useActiveChats(ws, activeWindow)
  const scoped = useMemo(() => (inScope ? active.filter((c) => inScope.has(c.account_id)) : active), [active, inScope])
  const groups = useMemo(() => activeChatGroups(scoped, { window: activeWindow, nowMs: ctx.nowMs }), [scoped, activeWindow, ctx.nowMs])
  // One group at a time: the fans who answered, or the chats waiting on the fan.
  const shown = mode === 'replied' ? groups.replied : groups.waiting
  const cards = useMemo(
    () => activeCards(mode === 'replied' ? { replied: groups.replied.slice(0, DOCK_ROWS), waiting: [] } : { replied: [], waiting: groups.waiting.slice(0, DOCK_ROWS) }, ctx),
    [groups, mode, ctx],
  )
  const modes = (
    <div className="ws-seg rc-side-seg" role="tablist" aria-label={tc('side.active.modes')}>
      {ACTIVE_MODES.map((m) => (
        <button key={m} type="button" role="tab" aria-selected={mode === m} className={mode === m ? 'is-on' : ''} onClick={() => ws.updateSettings({ [ACTIVE_MODE_KEY]: m })}>
          {tc(`side.active.${m}`)}
        </button>
      ))}
    </div>
  )
  return (
    <DockSection id="active" icon="messages-square" label={t('widgets.active')} sections={props.sections} onOpenWorkspace={props.onOpenWorkspace} before={modes}>
      <DockRows
        items={cards}
        total={shown.length}
        selectedKey={`c:${openKey}`}
        onOpen={open}
        onOpenWorkspace={props.onOpenWorkspace}
        empty={{ text: loaded ? tc(`side.active.empty.${mode}`) : t('page.loading'), loading: !loaded }}
      />
    </DockSection>
  )
}

export function FeedDock(props: DockProps) {
  const { t, ws, scope, ctx, open, openKey } = useDockBase(props)
  const { t: tc } = useTranslation('chat')
  const settings = useMemo(() => feedSettingsFrom(ws.settings.feed), [ws.settings.feed])
  const feed = useFeed({ enabled: ws.enabled && ws.prefsLoaded, settings, scope, onEvent: ws.onEvent, autoRun: false, openKey })
  const skip = useStableCallback((it: FeedItem) => feed.skip(it))
  const cards = useMemo(() => feedCards(feed.items.slice(0, DOCK_ROWS), settings, ctx, skip), [feed.items, settings, ctx, skip])
  const loading = !feed.loaded || (feed.loading && !feed.items.length) || feed.lists.some((l) => l.status === 'syncing' || l.status === 'refreshing')
  return (
    <DockSection id="feed" icon="sparkles" label={tc('side.blocks.feed')} sections={props.sections} onOpenWorkspace={props.onOpenWorkspace}>
      <DockRows
        items={cards}
        total={feed.items.length}
        selectedKey={`f:${openKey}`}
        onOpen={open}
        onOpenWorkspace={props.onOpenWorkspace}
        empty={{ text: feed.error ? tc('side.widget.suggestionsError') : loading ? t('page.loading') : tc('side.widget.suggestionsEmpty'), loading }}
      />
    </DockSection>
  )
}
